/**
 * Таблица именованных маршрутов сайта.
 *
 * Порядок в массиве — это приоритет сопоставления: первый подошедший паттерн выигрывает.
 * Поэтому `subClasses` должен идти раньше `classFeature`, иначе `/classes/:id/subclasses`
 * будет разобран как особенность класса с featureId = "subclasses".
 */
export type SiteRoutes = {
	index: {};

	registration: {};
	forgotPassword: {};
	resetPassword: {};
	account: {};
	authCallback: {provider: string};
	privacy: {};

	classes: {};
	class: {id: string};
	classInfo: {id: string};
	classSpells: {id: string};
	subClass: {id: string; subClassId: string};
	subClasses: {id: string};
	classFeature: {id: string; featureId: string};
	subClassFeature: {id: string; subClassId: string; featureId: string};

	classInvocations: {id: string};
	classInvocationFeature: {id: string; featureId: string};
	classMetaMagics: {id: string};
	classMetaMagicFeature: {id: string; featureId: string};
	classWildMagic: {id: string};
	classWildMagicFeature: {id: string; featureId: string};
	classManeuvers: {id: string};
	classManeuverFeature: {id: string; featureId: string};
	classArcaneShots: {id: string};
	classArcaneShotFeature: {id: string; featureId: string};

	spells: {};
	spell: {id: string};
	spellVariant: {id: string; variantId: string};
	spellsCards: {};

	species: {};
	specie: {id: string};
	specieInfo: {id: string};
	specieFeature: {id: string; featureId: string};

	origins: {};
	origin: {id: string};

	feats: {};
	feat: {id: string};

	sheets: {};
	sheet: {id: string};

	monsters: {};
	monster: {id: string};
	monsterInfo: {id: string};
	monsterVariant: {id: string; variantId: string};

	items: {};
	item: {id: string};
	itemVariant: {id: string; variantId: string};

	names: {};
	namesSet: {id: string};

	glossary: {};
	glossaryItem: {id: string};

	books: {};
	book: {bookId: string};
	bookChapter: {bookId: string; chapterId: string};

	trans14to24: {};

	/** строка запроса передаётся параметром q: /search?q=… */
	search: {};
};

export type RouteName = keyof SiteRoutes;

export type RouteParams<K extends RouteName> = SiteRoutes[K] & Partial<Record<string, string | number | null>>;

export type RouteDefinition = {
	/** имя маршрута, по нему строятся ссылки и определяется текущая страница */
	name: RouteName;
	/** паттерн адреса в формате path-to-regexp */
	pattern: string;
	/**
	 * Раздел сайта: при переходе внутри раздела query-строка (фильтры списка) переносится в новый адрес,
	 * при переходе в другой раздел — сбрасывается
	 */
	section?: string;
};

export const ROUTES: RouteDefinition[] = [
	{name: 'index', pattern: '/'},
	{name: 'registration', pattern: '/registration'},
	{name: 'forgotPassword', pattern: '/forgot-password'},
	{name: 'resetPassword', pattern: '/reset-password'},
	{name: 'account', pattern: '/account'},
	{name: 'authCallback', pattern: '/auth/callback/:provider'},
	{name: 'privacy', pattern: '/privacy'},

	{name: 'classes', pattern: '/classes', section: 'classes'},
	{name: 'classInfo', pattern: '/classes/:id/about', section: 'classes'},
	{name: 'classInvocations', pattern: '/classes/:id/invocations', section: 'classes'},
	{name: 'classInvocationFeature', pattern: '/classes/:id/invocations/:featureId', section: 'classes'},
	{name: 'classWildMagic', pattern: '/classes/:id/wild-magic', section: 'classes'},
	{name: 'classWildMagicFeature', pattern: '/classes/:id/wild-magic/:featureId', section: 'classes'},
	{name: 'classManeuvers', pattern: '/classes/:id/maneuvers', section: 'classes'},
	{name: 'classManeuverFeature', pattern: '/classes/:id/maneuvers/:featureId', section: 'classes'},
	{name: 'classMetaMagics', pattern: '/classes/:id/meta-magic', section: 'classes'},
	{name: 'classMetaMagicFeature', pattern: '/classes/:id/meta-magic/:featureId', section: 'classes'},
	{name: 'classArcaneShots', pattern: '/classes/:id/arcane-shots', section: 'classes'},
	{name: 'classArcaneShotFeature', pattern: '/classes/:id/arcane-shots/:featureId', section: 'classes'},
	{name: 'classSpells', pattern: '/classes/:id/spells', section: 'classes'},
	{name: 'subClasses', pattern: '/classes/:id/subclasses', section: 'classes'},
	{name: 'subClassFeature', pattern: '/classes/:id/subclasses/:subClassId/:featureId', section: 'classes'},
	{name: 'subClass', pattern: '/classes/:id/subclasses/:subClassId', section: 'classes'},
	{name: 'classFeature', pattern: '/classes/:id/:featureId', section: 'classes'},
	{name: 'class', pattern: '/classes/:id', section: 'classes'},

	{name: 'spells', pattern: '/spells', section: 'spells'},
	{name: 'spell', pattern: '/spells/:id', section: 'spells'},
	{name: 'spellVariant', pattern: '/spells/:id/:variantId', section: 'spells'},
	{name: 'spellsCards', pattern: '/staff/spell-cards'},

	{name: 'species', pattern: '/specie', section: 'species'},
	{name: 'specieInfo', pattern: '/specie/:id/about', section: 'species'},
	{name: 'specieFeature', pattern: '/specie/:id/:featureId', section: 'species'},
	{name: 'specie', pattern: '/specie/:id', section: 'species'},

	{name: 'origins', pattern: '/origins', section: 'origins'},
	{name: 'origin', pattern: '/origins/:id', section: 'origins'},

	{name: 'feats', pattern: '/feats', section: 'feats'},
	{name: 'feat', pattern: '/feats/:id', section: 'feats'},

	{name: 'sheets', pattern: '/sheets'},
	{name: 'sheet', pattern: '/sheets/:id'},

	{name: 'monsters', pattern: '/monsters', section: 'monsters'},
	{name: 'monsterInfo', pattern: '/monsters/:id/about', section: 'monsters'},
	{name: 'monster', pattern: '/monsters/:id', section: 'monsters'},
	{name: 'monsterVariant', pattern: '/monsters/:id/:variantId', section: 'monsters'},

	{name: 'items', pattern: '/items', section: 'items'},
	{name: 'item', pattern: '/items/:id', section: 'items'},
	{name: 'itemVariant', pattern: '/items/:id/:variantId', section: 'items'},

	{name: 'names', pattern: '/names', section: 'names'},
	{name: 'namesSet', pattern: '/names/:id', section: 'names'},

	{name: 'glossary', pattern: '/glossary', section: 'glossary'},
	{name: 'glossaryItem', pattern: '/glossary/:id', section: 'glossary'},

	{name: 'books', pattern: '/books', section: 'books'},
	{name: 'bookChapter', pattern: '/books/:bookId/:chapterId', section: 'books'},
	{name: 'book', pattern: '/books/:bookId', section: 'books'},

	{name: 'trans14to24', pattern: '/14-to-24'},

	{name: 'search', pattern: '/search'},
];

const byName = new Map<RouteName, RouteDefinition>(ROUTES.map((route) => [route.name, route]));

export function getRoute(name: RouteName): RouteDefinition {
	const route = byName.get(name);
	if (!route) throw new Error(`Неизвестный маршрут "${name}"`);
	return route;
}
